document.addEventListener("DOMContentLoaded", () => {
  const roots = document.querySelectorAll("[data-kxo-root]");
  if (!roots.length) return;

  // -------------------------
  // region uk detection
  // -------------------------
  const isUkRegion = () => {
    const country = String(window.Shopify?.country || "").toUpperCase();
    const activeCurrency =
      String(window.Shopify?.currency?.active || window.Shopify?.currency?.activeCurrency || "").toUpperCase();
    const locale = String(window.Shopify?.locale || document.documentElement.getAttribute("lang") || "").toLowerCase();

    return country === "GB" || activeCurrency === "GBP" || locale.includes("en-gb");
  };

  // -------------------------
  // money formatting (symbol after, except UK)
  // -------------------------
  const formatMoney = (cents, currency) => {
    const value = Number(cents) / 100;

    try {
      const parts = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "EUR",
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).formatToParts(value);

      const symbol = parts.find((p) => p.type === "currency")?.value || "";
      const number = parts.filter((p) => p.type !== "currency").map((p) => p.value).join("").trim();

      // UK: symbol before
      if (isUkRegion()) return `${symbol}${number}`.trim();

      // other: symbol after
      return `${number}${symbol}`.trim();
    } catch (e) {
      // fallback
      const num = value.toFixed(2);
      return isUkRegion() ? `£${num}` : `${num}€`;
    }
  };

  /**
   * In form mode we rely on the hidden input you already render:
   * <input type="hidden" name="selling_plan" value="" data-kxo-selling-plan-input>
   * We just ensure it stays enabled + synced.
   */
  const ensureKxoHiddenInForm = (form) => {
    if (!form) return null;

    let hidden = form.querySelector('input[name="selling_plan"][data-kxo-selling-plan-input]');
    if (!hidden) {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "selling_plan";
      hidden.setAttribute("data-kxo-selling-plan-input", "");
      hidden.value = "";
      form.appendChild(hidden);
    }

    hidden.disabled = false;
    return hidden;
  };

  // -------------------------
  // global fetch patch (main product only)
  // -------------------------
  const kxoFetchPatch = (() => {
    if (window.__kxoFetchPatchInstalled) return window.__kxoFetchPatchApi;
    window.__kxoFetchPatchInstalled = true;

    const contexts = new Map(); // variantId -> { getMode, getSellingPlanId }
    const originalFetch = window.fetch.bind(window);

    const normalizeUrl = (input) => {
      if (typeof input === "string") return input;
      if (input && typeof input.url === "string") return input.url;
      return "";
    };

    const isCartAddUrl = (url) => url.includes("/cart/add");

    const register = (variantId, ctx) => {
      if (!variantId) return;
      contexts.set(String(variantId), ctx);
    };

    window.fetch = async (input, init = {}) => {
      try {
        const url = normalizeUrl(input);
        if (!isCartAddUrl(url)) return originalFetch(input, init);

        const body = init.body;

        // A) JSON body
        if (typeof body === "string" && body.trim().startsWith("{")) {
          let obj = null;
          try {
            obj = JSON.parse(body);
          } catch (_) {}

          if (obj && Array.isArray(obj.items) && obj.items.length) {
            const matched = obj.items.find((it) => contexts.has(String(it.id)));
            if (matched) {
              const ctx = contexts.get(String(matched.id));
              const mode = ctx?.getMode?.();
              const spid = ctx?.getSellingPlanId?.();

              if (mode === "subscription" && spid) {
                matched.selling_plan = Number(spid);
              } else {
                delete matched.selling_plan;
              }

              init = { ...init, body: JSON.stringify(obj) };
            }
          }

          return originalFetch(input, init);
        }

        // B) FormData body
        if (body && typeof FormData !== "undefined" && body instanceof FormData) {
          const id = body.get("id");
          const variantId = id != null ? String(id) : "";

          if (variantId && contexts.has(variantId)) {
            const ctx = contexts.get(variantId);
            const mode = ctx?.getMode?.();
            const spid = ctx?.getSellingPlanId?.();

            if (mode === "subscription" && spid) {
              body.set("selling_plan", String(spid));
            } else {
              body.set("selling_plan", "");
            }

            init = { ...init, body };
          }

          return originalFetch(input, init);
        }
      } catch (_) {}

      return originalFetch(input, init);
    };

    const api = { register };
    window.__kxoFetchPatchApi = api;
    return api;
  })();

  // -------------------------
  // init each kxo root
  // -------------------------
  roots.forEach((root) => {
    if (root.__kxoInited) return;
    root.__kxoInited = true;

    const id = root.getAttribute("data-kxo-id");
    const mode = root.getAttribute("data-kxo-mode") || "ajax";
    const currency = root.getAttribute("data-kxo-currency") || "EUR";

    const spgIndex = parseInt(root.getAttribute("data-kxo-plan-group-index") || "0", 10);
    const spIndex = parseInt(root.getAttribute("data-kxo-plan-index") || "0", 10);

    const jsonEl = root.querySelector("script[data-kxo-product-json]");
    if (!jsonEl) return;

    let data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error("[kxo] invalid product json", e);
      return;
    }

    const variants = data.variants || [];
    const currentVariantId = (variants.find((v) => v.available) || variants[0] || {}).id || null;

    const spGroup = (data.selling_plan_groups || [])[spgIndex] || null;
    const sellingPlan = spGroup ? (spGroup.selling_plans || [])[spIndex] || null : null;

    const sellingPlanId =
      (root.getAttribute("data-kxo-selling-plan-id") || "").trim() ||
      (sellingPlan && sellingPlan.id ? String(sellingPlan.id) : "");

    const priceSubEl = root.querySelector('[data-kxo-price="subscription"]');
    const priceOTEl = root.querySelector('[data-kxo-price="onetime"]');
    const compareEl = root.querySelector(".kxo-price--compare");

    const getOnetimePrice = () => {
      const v = variants.find((v) => v.id === currentVariantId);
      return v ? v.price : null;
    };

    const getSubscriptionPrice = () => {
      if (!sellingPlanId) return null;
      const v = variants.find((v) => v.id === currentVariantId);
      if (!v || !v.selling_plan_allocations) return null;
      const alloc = v.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(sellingPlanId));
      if (alloc && alloc.per_delivery_price != null) return alloc.per_delivery_price;
      if (alloc && alloc.price != null) return alloc.price;
      return null;
    };

    const renderPrices = () => {
      const pOT = getOnetimePrice();
      const pSub = getSubscriptionPrice();

      if (priceOTEl && pOT != null) priceOTEl.textContent = formatMoney(pOT, currency);
      if (priceSubEl && pSub != null) priceSubEl.textContent = formatMoney(pSub, currency);

      if (compareEl) {
        if (pSub != null && pOT != null && pSub < pOT) {
          compareEl.textContent = formatMoney(pOT, currency);
          compareEl.style.display = "";
        } else {
          compareEl.textContent = "";
          compareEl.style.display = "none";
        }
      }
    };

    const readSelectedMode = () => {
      const checked = root.querySelector(`input[name="kxo-mode-${id}"]:checked`);
      return checked ? checked.value : "subscription";
    };

    const syncSellingPlanToMainForm = () => {
      if (mode !== "form") return;

      const form = root.closest('form[action^="/cart/add"]');
      if (!form) return;

      const hidden = ensureKxoHiddenInForm(form);
      if (!hidden) return;

      const selected = readSelectedMode();
      hidden.value = selected === "subscription" ? String(sellingPlanId || "") : "";
      hidden.disabled = false;
    };

    // initial
    renderPrices();
    syncSellingPlanToMainForm();

    // change
    root.querySelectorAll(`input[name="kxo-mode-${id}"]`).forEach((el) => {
      el.addEventListener("change", () => syncSellingPlanToMainForm());
    });

    // main product: patch fetch for add-to-cart flows
    if (mode === "form") {
      const form = root.closest('form[action^="/cart/add"]');
      const formVariantId =
        (form && form.querySelector('input[name="id"]') && form.querySelector('input[name="id"]').value) ||
        currentVariantId;

      if (formVariantId) {
        kxoFetchPatch.register(formVariantId, {
          getMode: () => readSelectedMode(),
          getSellingPlanId: () => sellingPlanId,
        });
      }

      if (form && !form.__kxoSellingPlanSubmitHooked) {
        form.__kxoSellingPlanSubmitHooked = true;
        form.addEventListener(
          "submit",
          () => {
            syncSellingPlanToMainForm();
          },
          true
        );
      }
    }

    // featured (ajax) — keep as-is
    const atcBtn = root.querySelector("[data-kxo-atc]");
    if (mode === "ajax" && atcBtn) {
      const cartEle = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

      atcBtn.addEventListener("click", async () => {
        const selectedMode = readSelectedMode();

        if (!currentVariantId) {
          alert("Variant unavailable");
          return;
        }

        const item = { id: currentVariantId, quantity: 1 };

        if (selectedMode === "subscription") {
          if (!sellingPlanId) {
            alert("Subscription not available for this product.");
            return;
          }
          item.selling_plan = sellingPlanId;
        }

        atcBtn.disabled = true;
        atcBtn.textContent = "Adding...";

        try {
          const sections =
            cartEle?.getSectionsToRender?.().map((s) => s.id) || ["cart-drawer", "cart-icon-bubble"];

          const payload = {
            items: [item],
            sections,
            sections_url: window.location.pathname,
          };

          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload),
          });

          if (!res.ok) throw new Error("ATC failed");

          atcBtn.textContent = "Added ✓";
          setTimeout(() => {
            atcBtn.textContent = "Add to cart";
            atcBtn.disabled = false;
          }, 900);

          const json = await res.json();

          if (cartEle && typeof cartEle.renderContents === "function") {
            if (cartEle.classList.contains("is-empty")) cartEle.classList.remove("is-empty");
            if (window.CartPerformance?.measure) {
              window.CartPerformance.measure("add:paint-updated-sections", () => cartEle.renderContents(json));
            } else {
              cartEle.renderContents(json);
            }
          } else {
            document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { openDrawer: true } }));
          }
        } catch (e) {
          console.error(e);
          atcBtn.disabled = false;
          atcBtn.textContent = "Add to cart";
          alert("Could not add to cart.");
        }
      });
    }
  });
});
